import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactMenu,
  CompactSearch,
  CompactTitle,
  DataTable,
  HelpPopover,
  NotPermitted,
  PageHeader,
  SelectChip,
  STICKY_BAR_TOP,
  STICKY_TABLE_TOP,
  StatusChip,
  ToggleChip,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  WorkControls,
  openRow,
  useFollow,
  useOpenParam,
  PaneControl,
  PaneSplit,
  RowOpens,
  usePanePrefs
} from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { listsApi } from '../../api/lists-api';
import type { IListFieldCatalogue, IListRow } from '../../api/lists-contracts';
import type {
  FirmStatus,
  IMetricsEdition,
  IAlsoOwes,
  IMetricsEditionKey,
  IMetricsProgress,
  IMetricsRowsRequest,
  IThresholdCondition,
  IMetricsRows,
  IMetricsUpdates,
  MetricsTab
} from '../../api/metrics-updates-contracts';
import { metricsUpdatesApi } from '../../api/metrics-updates-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { useCardVariant } from '../../components/state-cards';
import { formatDay } from '../../components/days';
import { recordSurveyPath } from '../../components/participant-link';
import { LIST_MAX_PAGE_SIZE, LIST_SURVEYS, listSurveyLabel } from '../../lists';
import {
  DATA_AS_OF,
  FIRM,
  FIRM_STATUS,
  LAST_REQUEST,
  REQUESTS,
  conditionsOf,
  firmStatusChip,
  pairKey,
  relationWords,
  seriesKey,
  seriesLabel,
  thresholdWords
} from '../../metrics-updates';
import { TEAM_PATHS, rankingPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { BulkAssign } from '../dd/dd-calls-page';
import { cellText } from '../lists/cells';
import { ASSIGNED_TO } from '../lists/editing';
import { SurveyMenu, SurveyTabs } from '../lists/area';
import { rowKeyOf } from '../lists/use-list-rows';
import { useWorkColumns } from '../lists/work-page/columns';
import {
  WorkAdvancedSheet,
  WorkFilters,
  WorkSearch,
  quickChip,
  workFilterChips,
  workHeads,
  type IWorkScope
} from '../lists/work-page/controls';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import { PAGING, WorkPager, dropPaging, readPages, useWorkPaging } from '../lists/work-page/pager';
import { SelectionBar } from '../lists/work-page/selection-bar';
import { signalsOn, toggledSignals } from '../lists/work-page/signals';
import { useWorkPage, useWorkSearch } from '../lists/work-page/use-work-page';
import { MetricsFirmsTab } from './firms-tab';
import { MetricsPanel } from './panel';
import { MailMergeButton } from './mail-merge';
import { STAGE_TILES, STAGE_VIEWS, StageNav, StageTotals, type StageView } from './stage-strip';
import { STAGE_SIGNALS, useStageSignals } from './card-signals';
import { WRITE_REASON, assignRows, RowActions, recordRequests } from './row-actions';

/**
 * Cycle → Metrics updates (SR-315; PRS v1.6 RNK-07 and WF-10): who still owes the survey's
 * figures for the round's editions, and the safety net for those ranked before whose figures fell
 * under a threshold.
 *
 * **The round is survey-api's**: the newest open or closed survey cycle with an as-of date, said in
 * the header. Its editions are listed with checkboxes, every one holding a threshold ticked unless
 * the URL unticks it (`?off=<list>.<year>,…`), each with its threshold in words and its editor; a
 * row on any ticked edition is listed once, its editions named. **Who is on either list is
 * survey-api's too** — eligibility, the firm's status in the round, each list's template, its
 * threshold and margin, whether the figures were received — composed there from what the page
 * sends: the editions, the list, "DD call done", the search and whose rows. Nothing is filtered,
 * counted or evaluated here.
 *
 * **The stage is the page's navigation** (`stage-strip.tsx`, `?view=`): its totals, then its four
 * worklists as tiles — the advisors to ask (the default), those whose firm has no status, the
 * firms whose files are awaited, the exceptions — each with survey-api's count, the open one
 * marked as a tab is.
 *
 * **A row's work is three presses** (`row-actions.tsx`): a request recorded (outreach, purpose
 * `metrics`), the figures entered at the round's as-of date, or the figures confirmed unchanged.
 * The last two set "Data as of", and the list read again no longer holds the row — said by name.
 * Assignment is the Lists' own `sys.assigned_to` cell, DD calls' bar.
 *
 * **Owed by firm files** (`firms-tab.tsx`) is where a firm's status in the round is set, and where a
 * firm's file is seen to have come in.
 *
 * **The page scrolls, its table's header sticking** under the app bar. The header is one row —
 * title, survey tabs, actions — with the summary line under it; once it, the cards and the filters
 * have scrolled away, the compact bar (`condense.tsx`) draws them in the app bar's row: the title,
 * the survey as a menu, the open list as one chip holding the others, the search, the quick filters
 * behind *Filters*, *Advanced…*, *Columns* and the actions as icons — and the filters applied on a
 * second row, as under the toolbar. "DD call done" is on by default: its chip says it turned off.
 */

const SURVEYS = LIST_SURVEYS.filter((survey) => survey.code !== 'CANADA_FR');
const NAME = 'sys.display_name';
/** What the table leaves to the panel while one is open beside it. */
const PANEL_HIDES = new Set(['editions', 'requests', 'assigned', 'data-as-of', 'actions']);
const COLUMNS = [NAME, 'sys.city', 'sys.state', FIRM, FIRM_STATUS, DATA_AS_OF, REQUESTS, LAST_REQUEST, ASSIGNED_TO];

type Whose = 'all' | 'me' | 'none';
type ListedFirm = Exclude<FirmStatus, 'supplies'>;

/** What a rows read answered, keyed by the request it answers. */
type RowsRead =
  | { asked: string; survey: string; kind: 'rows'; answer: IMetricsRows }
  | { asked: string; survey: string; kind: 'refused'; sentence: string; threshold: boolean };

/** The stage's list the URL opens (`?view=`), the advisors to ask where it names none. */
function viewOf(params: URLSearchParams): StageView {
  const view = params.get('view');
  return STAGE_VIEWS.find((entry) => entry === view) ?? 'direct';
}

function whoseOf(params: URLSearchParams): Whose {
  const whose = params.get('whose');
  return whose === 'me' || whose === 'none' ? whose : 'all';
}

function editionKey(edition: Pick<IMetricsEdition, 'list' | 'year'>): string {
  return `${edition.list}.${edition.year}`;
}

export function MetricsUpdatesPage() {
  const [params, setParams] = useSearchParams();
  const survey = params.get('survey') ?? 'FA';
  const view = viewOf(params);
  // A list of the stage as survey-api reads it: the advisors to ask, by their firm's status —
  // asked directly, or no status known — or the exceptions. The Firms view reads the first, for
  // the stage's counts.
  const tab: MetricsTab = view === 'exceptions' ? 'exceptions' : 'updates';
  const firm: ListedFirm[] = view === 'unknown' ? ['not_set', 'no_firm'] : ['direct'];
  const whose = whoseOf(params);
  // "DD call done" is on unless the URL turns it off (`?dd=off`).
  const ddDone = params.get('dd') !== 'off';
  // The search and the page's own filters, and the window — the page and the rows *Load more*
  // added — in the URL.
  const { q, set, search: searched } = useWorkSearch();
  const paging = useWorkPaging({ replace: true });
  const { page, perPage, pages } = paging;
  const [recording, setRecording] = React.useState(false);
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [reread, setReread] = React.useState(0);
  const can = useCan();
  const canAssign = can('internal_fields.write') && can('lists.assign');
  // A catalogue read for another survey is none: a survey switched never words its cells by the last.
  const fieldsRead = useAdminData(
    async (signal) => ({ survey, fields: await listsApi.fields(survey, signal) }),
    [survey]
  );
  const catalogue = fieldsRead.data?.survey === survey ? fieldsRead.data.fields : null;
  const unread = React.useMemo<IListFieldCatalogue>(() => ({ survey, groups: [] }), [survey]);
  // Lists' controls beside the page's own: a filter and a column sort sent to survey-api, extra columns.
  const work = useWorkPage(survey, catalogue ?? unread, { replace: true, drop: PAGING });
  React.useEffect(() => {
    setTicked((now) => (now.size > 0 ? new Set() : now));
    setSaid(null);
  }, [survey, view, whose, ddDone, q, page, params.get('off'), work.criteria, work.sorts]);

  const round = useAdminData((signal) => metricsUpdatesApi.round(survey, signal), [survey]);
  // When the round was last read — the header's "updated" before any row is.
  const roundAt = useReadAt(round.data);
  const updates = round.data?.surveyCode === survey ? round.data : null;
  // Every edition of the round is ticked but those the URL unticks (`?off=<list>.<year>,…`); an
  // edition with no threshold cannot be read, and is never sent.
  const off = new Set((params.get('off') ?? '').split(',').filter(Boolean));
  const readable = (updates?.editions ?? []).filter((entry) => entry.metricThreshold !== null);
  const included = readable.filter((entry) => !off.has(editionKey(entry)));
  const sent = included.length === readable.length ? undefined : included.map(({ list, year }) => ({ list, year }));

  // The round's seeded view in Lists, opened from the header.
  const seeded = useAdminData(
    async (signal) =>
      (await listsApi.listViews(survey, 'list', signal))?.find((one) => one.seed === 'metrics_updates')?.id ?? null,
    [survey]
  );
  const accounts = useAdminData(async (signal) => (canAssign ? listsApi.assignees(signal) : null), [canAssign]);
  const fields = work.fields;
  const text = (row: IListRow, code: string) => {
    const field = fields.get(code);
    return field ? cellText(field, row.cells[code]) : String(row.cells[code]?.v ?? '');
  };
  const place = (row: IListRow) => [text(row, 'sys.city'), text(row, 'sys.state')].filter(Boolean).join(', ');

  // A series no row has a figure for yet is not in the catalogue: its column is named by the survey's series.
  const columnLabel = (code: string) => {
    const [, metric, scope] = code.split('.');
    return seriesLabel(updates?.figureSeries ?? [], metric, scope);
  };
  const thresholdSeries = uniqueSeries(included);
  const thresholdFields = thresholdSeries.map((condition) => `fig.${condition.metric}.${condition.scope}`);
  const fixed = [...COLUMNS, ...thresholdFields];
  // The viewer's columns, kept on this browser per survey.
  const chosen = useWorkColumns(work, `metrics-updates.${survey}`, fixed);
  const extras = chosen.extras;
  const listed =
    included.length > 0
      ? JSON.stringify([survey, sent ?? null, tab, firm, ddDone, q, whose, page, perPage, work.criteria, work.sorts, extras])
      : null;
  const asked = listed === null ? null : `${listed}|${pages}`;
  // The list's own scoping — what its funnels count under, too.
  const scoping = {
    ...(sent ? { editions: sent } : {}),
    tab,
    firm,
    ddDone: tab === 'updates' && updates?.ddApplies === true ? ddDone : undefined,
    q: q || undefined,
    assigned: whose === 'all' ? undefined : whose
  } as const;
  // What the rows read and the mail merge both send: the scoping, Lists' filter and column sort.
  const request: Omit<IMetricsRowsRequest, 'page' | 'pageSize'> = {
    ...scoping,
    ...(work.criteria ? { criteria: work.criteria } : {}),
    ...(work.sorts.length > 0
      ? { sort: work.sorts.map(({ field, dir }) => ({ field, dir: dir === 'desc' ? 'desc' : 'asc' })) }
      : {}),
    columns: [...fixed, ...extras]
  };
  const scope: IWorkScope = {
    query: { survey },
    base: null,
    facets: ({ query, field, q: search }, signal) =>
      metricsUpdatesApi.facets(
        survey,
        {
          ...scoping,
          ...(query.criteria?.children.length ? { criteria: query.criteria } : {}),
          field,
          ...(search ? { search } : {})
        },
        signal
      )
  };
  const head = workHeads(work, scope);
  const rows = useAdminData<Nullable<RowsRead>>(
    async (signal) => {
      if (asked === null) return null;
      try {
        const parts = await readPages(
          (at, pageSize) => metricsUpdatesApi.rows(survey, { ...request, page: at, pageSize }, signal),
          page,
          perPage,
          pages,
          LIST_MAX_PAGE_SIZE
        );
        const answer: IMetricsRows = {
          ...parts[0],
          rows: parts.flatMap((part) => part.rows),
          editions: Object.assign({}, ...parts.map((part) => part.editions)),
          alsoOwes: Object.assign({}, ...parts.map((part) => part.alsoOwes))
        };
        return { asked, survey, kind: 'rows', answer };
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          const threshold = error.isProblem(ProblemType.thresholdMissing);
          return {
            asked,
            survey,
            kind: 'refused',
            threshold,
            sentence: threshold
              ? 'No edition ticked holds a threshold: set one to read who owes figures.'
              : refusalMessage(error, 'The list could not be read.')
          };
        }
        throw error;
      }
    },
    // A Refresh or a write reads the same query again, the answer drawn meanwhile.
    [asked, reread]
  );
  // The last answer while the next is out, dimmed — of this survey; none once this query's read failed.
  const read =
    rows.data?.survey === survey && asked !== null && !(rows.error && rows.data.asked !== asked) ? rows.data : null;
  const answer = read?.kind === 'rows' ? read.answer : null;
  const drawn = answer?.rows ?? [];
  const tickedRows = drawn.filter((row) => ticked.has(rowKeyOf(row)));
  const advisor = (row: IListRow) => text(row, NAME) || row.participantId;
  const [readAt, setReadAt] = React.useState<Nullable<Date>>(null);
  // The stage's counts as last read — kept on the Firms tab, which reads no rows.
  const [progress, setProgress] = React.useState<Nullable<IMetricsProgress>>(null);
  React.useEffect(() => {
    if (answer) setReadAt(new Date());
    if (answer?.progress) setProgress(answer.progress);
  }, [answer]);
  React.useEffect(() => setProgress(null), [survey, params.get('off')]);

  const readAgain = () => setReread((count) => count + 1);
  const moved = (sentence: string) => {
    setSaid(sentence);
    readAgain();
  };

  // The row opened beside the list, in the address (`?open=<participant>:<survey>`), as last read —
  // else as last drawn, once a write took it off the page (its figures came in) — and its
  // neighbours on the page. Another survey, stage or page closes it.
  const panel = useOpenParam(answer?.rows ?? null, rowKeyOf);
  const panelRow = panel.row;
  const setOpened = (row: Nullable<IListRow>, replace = false) => (row ? panel.open(row, { replace }) : panel.close());
  const elsewhere = JSON.stringify([survey, view, page, params.get('off')]);
  const firstPlace = React.useRef(elsewhere);
  const closePanel = panel.close;
  React.useEffect(() => {
    if (firstPlace.current === elsewhere) return;
    firstPlace.current = elsewhere;
    closePanel({ replace: true });
  }, [elsewhere, closePanel]);
  // ↑ / ↓ in the table walk the open panel, following where they stop.
  const later = useFollow();
  const follow = (event: React.FocusEvent) => {
    if (!panelRow) return;
    const key = (event.target as HTMLElement).closest('tr')?.getAttribute('data-row-key');
    const row = key ? drawn.find((one) => rowKeyOf(one) === key) : undefined;
    if (row && rowKeyOf(row) !== rowKeyOf(panelRow)) later(() => setOpened(row, true));
  };
  // Where a row opens and how the panel is laid out — the reader's one choice for every work page.
  const panePrefs = usePanePrefs();
  // Beside an open panel the list keeps what the round is worked by; below or as a sheet it has room.
  const beside = panelRow !== null && panePrefs.layout === 'beside';
  /** A row's press, Enter or a plain click on its name: the panel, or the record in a new tab. */
  const openOne = (row: IListRow) => openRow(panePrefs, recordSurveyPath(row.participantId, row.survey), () => setOpened(row));
  const panelIndex = panelRow ? drawn.findIndex((row) => rowKeyOf(row) === rowKeyOf(panelRow)) : -1;
  const editionsOf = (row: IListRow) => answer?.editions?.[pairKey(row)] ?? [];
  const alsoOwesOf = (row: IListRow) => answer?.alsoOwes?.[pairKey(row)] ?? [];

  const columns: IDataColumn<IListRow>[] = [
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      className: 'w-0 whitespace-nowrap',
      cell: (row) => (
        <RowOpens
          name={advisor(row)}
          open={panelRow !== null && rowKeyOf(panelRow) === rowKeyOf(row)}
          onPane={() => setOpened(row)}
          to={recordSurveyPath(row.participantId, row.survey)}
          tabWords={`Open ${advisor(row)} — ${listSurveyLabel(row.survey)} in a new tab`}
        />
      )
    },
    {
      id: 'name',
      ...head(NAME, 'Advisor'),
      className: 'whitespace-nowrap',
      cell: (row) => (
        <span className="grid">
          <Link
            className="font-medium underline-offset-2 hover:underline"
            to={recordSurveyPath(row.participantId, row.survey)}
            // A plain click follows *Opens in*, as the row's; Ctrl/⌘ or the middle button keep the link's.
            onClick={(event) => {
              if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              openOne(row);
            }}
          >
            {advisor(row)}
          </Link>
          <span className="text-[0.75rem] text-muted-foreground">{place(row)}</span>
          <AlsoOwes entries={alsoOwesOf(row)} />
        </span>
      )
    },
    {
      id: 'editions',
      header: 'Editions',
      cell: (row: IListRow) => {
        const on = editionsOf(row);
        return (
          <span className="flex flex-wrap gap-1" data-row-editions={on.map(editionKey).join(' ')}>
            {on.map((entry) => (
              <StatusChip key={editionKey(entry)} tone="neutral">
                {editionLabel(updates, entry)}
              </StatusChip>
            ))}
          </span>
        );
      }
    },
    ...(updates?.firmApplies
      ? [
          {
            id: 'firm',
            ...head(FIRM, 'Firm'),
            cell: (row: IListRow) => {
              const status = row.cells[FIRM_STATUS]?.v;
              const chip = typeof status === 'string' ? firmStatusChip(status) : null;
              return (
                <span className="grid justify-items-start gap-1">
                  <span>{text(row, FIRM)}</span>
                  {chip && (
                    <span data-firm-status={String(status)}>
                      <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                    </span>
                  )}
                </span>
              );
            }
          }
        ]
      : []),
    ...(answer?.thresholdFields ?? thresholdFields).map((code) => ({
      id: code,
      ...head(code, fields.get(code)?.label ?? columnLabel(code)),
      numeric: true,
      cell: (row: IListRow) => text(row, code)
    })),
    {
      id: 'data-as-of',
      ...head(DATA_AS_OF, 'Data as of'),
      className: 'whitespace-nowrap',
      cell: (row) => {
        const value = row.cells[DATA_AS_OF]?.v;
        return typeof value === 'string' ? formatDay(value.slice(0, 10)) : <span className="text-muted-foreground">None</span>;
      }
    },
    {
      id: 'requests',
      ...head(REQUESTS, 'Requests'),
      className: 'whitespace-nowrap',
      cell: (row) => {
        const count = Number(row.cells[REQUESTS]?.v ?? 0);
        const last = row.cells[LAST_REQUEST]?.v;
        if (!count) return <span className="text-muted-foreground">None sent</span>;
        return (
          <span className="grid">
            <span>{count} sent</span>
            {typeof last === 'string' && (
              <span className="text-[0.75rem] text-muted-foreground">last {formatDay(last.slice(0, 10))}</span>
            )}
          </span>
        );
      }
    },
    {
      id: 'assigned',
      ...head(ASSIGNED_TO, 'Assigned to'),
      cell: (row) => text(row, ASSIGNED_TO) || <span className="text-muted-foreground">Nobody</span>
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      label: 'Actions',
      className: 'w-0 whitespace-nowrap',
      cell: (row) =>
        updates?.round && (
          <RowActions
            row={row}
            name={advisor(row)}
            survey={survey}
            asOf={updates.round.asOf}
            series={updates.figureSeries}
            thresholdSeries={thresholdSeries}
            exceptions={view === 'exceptions'}
            onReview={() => setOpened(row)}
            onDone={moved}
          />
        )
    }
  ];

  // The row's two ways in (the panel, ↗ a new tab) are no column the team arranges: drawn first.
  const shown = [
    ...columns.filter((column) => column.id === 'open'),
    ...chosen.resolve(
      columns.filter((column) => column.id !== 'open'),
      scope
    )
  ];

  const state: DataTableState | undefined = rows.error && !read
    ? { kind: 'error', content: rows.error }
    : read?.kind === 'refused'
      ? { kind: 'empty', content: read.sentence }
      : !answer
        ? { kind: 'loading' }
        : {
            kind: 'empty',
            content:
              view === 'exceptions'
                ? 'No advisor ranked before is under the threshold.'
                : view === 'unknown'
                  ? 'Every advisor who owes figures is at a firm with a status.'
                  : q
                    ? `No one matching “${q}” owes figures on this list — they may be on another list above, or under every threshold read.`
                    : 'Nobody owes figures under these filters.'
          };

  const views = updates?.firmApplies === false ? (['direct', 'exceptions'] as const) : STAGE_VIEWS;
  const [variant] = useCardVariant();
  const pressed = signalsOn(work, STAGE_SIGNALS).map((one) => one.key);
  const signals = useStageSignals({
    survey,
    views,
    editions: sent,
    ddDone: updates?.ddApplies === true ? ddDone : undefined,
    firmApplies: updates?.firmApplies !== false,
    variant,
    enabled: updates?.round != null && included.length > 0,
    counted: String(reread),
    on: (card, key) => card === view && pressed.includes(key)
  });
  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  /** The address with another list open — a tile's link, and its signal's press. */
  const opening = (search: URLSearchParams, next: StageView) => {
    if (next === 'direct') search.delete('view');
    else search.set('view', next);
    search.delete('tab');
    search.delete('firm');
    dropPaging(search);
  };
  const hrefFor = (next: StageView) => {
    const search = new URLSearchParams(params);
    opening(search, next);
    return `?${search.toString()}`;
  };
  // Each tile's signals as toggles: a press adds its condition to the signals' group, opening its list.
  const toggles =
    signals &&
    Object.fromEntries(
      views.map((key) => [
        key,
        toggledSignals({
          page: work,
          params,
          setParams: (next) => setParams(dropPaging(next), { replace: true }),
          current: key === view,
          signals: signals[key],
          specs: STAGE_SIGNALS,
          pressed,
          openCard: (next) => opening(next, key),
          nameOf: (signal) =>
            `Show ${signal.count.toLocaleString('en-US')} ${signal.label} in ${STAGE_TILES[key].label}`
        }) ?? []
      ])
    );

  const summary = updates && (
    updates.round ? (
      <EditionPicker
        updates={updates}
        off={off}
        onToggle={(key, on) => {
          const next = new Set(off);
          if (on) next.delete(key);
          else next.add(key);
          set({ off: next.size > 0 ? [...next].join(',') : null });
        }}
        lead={<RoundLine updates={updates} />}
        totals={included.length > 0 ? <StageTotals progress={progress} /> : null}
      />
    ) : (
      <RoundLine updates={updates} />
    )
  );

  const lists = seeded.data ? { to: `${TEAM_PATHS.lists}?${new URLSearchParams({ survey, view: seeded.data })}` } : null;
  const refresh = {
    reading: rows.reading || round.reading,
    readAt: readAt ?? roundAt,
    onClick: () => {
      readAgain();
      void round.reload();
    }
  };
  const help = (
    <HelpPopover label="About Metrics updates" align="end">
      <p>
        Who still owes the survey’s figures for the round’s editions, and the safety net for those ranked
        before whose figures fell under a threshold.
      </p>
      <p>
        Each card is a list of the stage with its count over the ticked editions; its badges count the advisors
        in it not asked yet, asked with no reply, or unassigned — press one to show only those, and another to
        show those too; press it again, or its ✕, to drop it — the counts stay as they are. Hover a card for
        what to do with it.
      </p>
      <p>
        A row&apos;s panel opens by its first icon, and the advisor&apos;s record in a new tab by ↗. A click on the
        row or the name goes where «Opens in» says, which also sets where the panel sits: beside the list,
        below it, or a sheet over it (the default under 1440 px).
      </p>
      <p>
        Scrolled down, the header, the cards and the filters ride slimmed in the bar at the top of the window,
        with the table’s column names under them.
      </p>
    </HelpPopover>
  );
  const showsList = Boolean(updates?.round && view !== 'firms' && included.length > 0);
  const stage = updates?.round && included.length > 0 && (
    <StageNav progress={progress} view={view} views={views} hrefFor={hrefFor} signals={toggles} />
  );
  // The toolbar's quick filters, drawn in the page and again in the compact bar.
  const quickFilters = (
    <>
      {updates?.ddApplies && tab === 'updates' && (
        <ToggleChip
          label="DD call done"
          checked={ddDone}
          onChange={(on) => set({ dd: on ? null : 'off' })}
        />
      )}
      <SelectChip
        label="Assigned"
        any="Anyone"
        value={whose === 'all' ? null : whose}
        options={[
          { value: 'me', label: 'Me' },
          { value: 'none', label: 'Nobody' }
        ]}
        onChoose={(value) => set({ whose: value })}
      />
    </>
  );
  const search = {
    label: 'Filter advisors',
    placeholder: 'Filter advisors…',
    filters: true,
    ...searched
  };
  const clearAll = () => set({ q: null, whose: null, dd: null, f: null });
  // Every filter applied, as a chip with its value — the page's row under the toolbar and the
  // compact bar's second row alike: a quick filter's press opens its control, a Lists chip its
  // column's funnel.
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const quickChipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...(updates?.ddApplies && tab === 'updates' && !ddDone
      ? [quickChip('DD call done', 'Any', open, () => set({ dd: null }))]
      : []),
    ...(whose === 'all'
      ? []
      : [quickChip('Assigned', whose === 'me' ? 'Me' : 'Nobody', open, () => set({ whose: null }))])
  ];
  const appliedWith = (open: (label: string) => true) =>
    catalogue ? workFilterChips(work, STAGE_SIGNALS, quickChipsWith(open)) : quickChipsWith(open);
  const quickCount = quickChipsWith(openFilters).length;
  const appliedChips = appliedWith(openFilters);
  const pageChips = appliedWith(toolbar.openQuick);
  // A list's count as its tile says it, for the compact bar's chip and its menu.
  const progressOf = (key: StageView) =>
    progress ? STAGE_TILES[key].count(progress).toLocaleString('en-US') : '—';
  const columnsButton = chosen.button(columns);

  return (
    <>
      <PageHeader
        title="Metrics updates"
        row
        tabs={<SurveyTabs current={survey} surveys={SURVEYS} />}
        line={summary}
        actions={
          <WorkPageHeaderActions
            summary={progress ? `${progress.inScope.toLocaleString('en-US')} in scope` : null}
            summaryData={{ 'data-metrics-summary': '' }}
            lists={lists}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled && showsList}
        label="Metrics updates, compact"
        actions={<WorkPageHeaderActions summary={null} lists={lists} refresh={refresh} help={help} />}
        below={appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />}
      >
        <CompactTitle>Metrics updates</CompactTitle>
        <SurveyMenu current={survey} surveys={SURVEYS} />
        <CompactMenu
          label="Stage"
          chip
          trigger={
            <>
              {STAGE_TILES[view].label}
              <span className="font-semibold tabular-nums">{progressOf(view)}</span>
            </>
          }
          items={views.map((key) => ({
            key,
            label: STAGE_TILES[key].label,
            to: hrefFor(key),
            replace: true,
            current: key === view,
            count: progressOf(key)
          }))}
        />
        <CompactDivider />
        <CompactSearch {...search} />
        {/* One Filters menu (SR-530): the quick filters, the field search, Advanced… and Clear all. */}
        <WorkFilters
          page={work}
          quick={quickFilters}
          count={appliedChips.length}
          onClearAll={clearAll}
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
        />
        <span className="flex-1" />
        <CompactDivider />
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {round.error && !updates && <LoadError message={round.error} />}
        {updates && (
          <div className="grid gap-3">
            {stage}
            {updates.round && view === 'firms' && <MetricsFirmsTab survey={survey} reread={reread} onWritten={readAgain} />}
            {updates.round && readable.length > 0 && included.length === 0 && (
              <p className="text-sm text-muted-foreground" data-metrics-none-ticked>
                Tick an edition to read who owes figures for it.
              </p>
            )}
            {showsList && (
              <div className="grid gap-3">
                <WorkControls end={setControlsEnd}>
                  <WorkToolbar
                    toolbar={toolbar}
                    search={<WorkSearch search={search} />}
                    quick={quickFilters}
                    quickCount={quickCount}
                    // One Filters menu (SR-530): the quick filters fold into it when the row is short.
                    menu={(folded) => (
                      <WorkFilters
                        page={work}
                        quick={folded}
                        count={pageChips.length}
                        onClearAll={clearAll}
                        open={toolbar.filtersOpen}
                        onOpenChange={toolbar.setFiltersOpen}
                      />
                    )}
                    end={
                      <>
                        {/* The open card counts the list: a reader is told only that a read is out. */}
                        <span role="status" className="sr-only">
                          {rows.reading && <span data-list-reading="">Searching…</span>}
                        </span>
                        <HelpPopover label={`About ${STAGE_TILES[view].label}`} align="end">
                          <p>
                            {STAGE_TILES[view].label}: {STAGE_TILES[view].hint}.
                          </p>
                          {view === 'unknown' && (
                            <p data-unknown-note>
                              These advisors’ firms have no status in this round, or they name no firm. Set the firm’s
                              status under{' '}
                              <Link className="underline" to={hrefFor('firms')} replace>
                                Owed by firm files
                              </Link>{' '}
                              — a firm that doesn’t receive metrics moves its advisors to Owed by advisors.
                            </p>
                          )}
                          {tab === 'exceptions' && (
                            <p data-exceptions-note>
                              Ranked before on a ticked list, and now under its threshold — often a typo in the survey. An
                              advisor stays here, survey submitted or not, until the figures are checked: corrected with
                              “Enter figures…” or confirmed with “Confirm unchanged…”.
                            </p>
                          )}
                        </HelpPopover>
                        <MailMergeButton
                          survey={survey}
                          request={request}
                          ticked={tickedRows}
                          total={answer?.total ?? 0}
                          onDone={moved}
                        />
                        {columnsButton}
                        <PaneControl prefs={panePrefs} />
                      </>
                    }
                  />
                  {pageChips.length > 0 && (
                    <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />
                  )}
                </WorkControls>
                <WorkAdvancedSheet page={work} />
                {!canAssign && <NotPermitted id="metrics-assign-not-permitted" what="assigning advisors" />}
                {!can('internal_fields.write') && (
                  <NotPermitted id={WRITE_REASON} what="recording requests and figures" />
                )}
                <SelectionBar
                  count={tickedRows.length}
                  onClear={() => setTicked(new Set())}
                  stickyTop={STICKY_BAR_TOP}
                >
                  <BulkAssign
                    count={tickedRows.length}
                    accounts={accounts.data}
                    accountsUnread={accounts.error !== null}
                    off={!canAssign || accounts.data === null}
                    locked={!canAssign}
                    reasonId="metrics-assign-not-permitted"
                    onAssign={async (accountId) => moved(await assignRows(tickedRows, accountId, accounts.data ?? []))}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!can('internal_fields.write') || recording}
                    aria-describedby={can('internal_fields.write') ? undefined : WRITE_REASON}
                    onClick={() => {
                      setRecording(true);
                      void recordRequests(tickedRows, survey)
                        .then(moved)
                        .finally(() => setRecording(false));
                    }}
                  >
                    Record a request for the selected
                  </Button>
                </SelectionBar>
                {said && (
                  <p role="status" className="text-sm text-[var(--color-teal)]" data-metrics-said>
                    {said}
                  </p>
                )}
                {rows.error && read && (
                  <LoadError message={`Could not refresh — showing the list as last read. ${rows.error}`} />
                )}
                {answer?.skipped && answer.skipped.length > 0 && (
                  <p className="text-sm text-muted-foreground" data-metrics-skipped>
                    Not read, for holding no threshold: {answer.skipped.map((entry) => entry.name).join(', ')}.
                  </p>
                )}
                <PaneSplit
                  prefs={panePrefs}
                  title={panelRow ? advisor(panelRow) : 'Metrics update'}
                  width="min(36vw,32rem)"
                  onClose={() => setOpened(null)}
                  pane={
                    panelRow &&
                    updates.round && (
                      <MetricsPanel
                        key={rowKeyOf(panelRow)}
                        layout={panePrefs.layout}
                        row={panelRow}
                        name={advisor(panelRow)}
                        meta={[text(panelRow, FIRM), place(panelRow)].filter(Boolean).join(' · ')}
                        editions={editionsOf(panelRow).map((entry) => editionLabel(updates, entry))}
                        alsoOwes={alsoOwesOf(panelRow)}
                        survey={survey}
                        asOf={updates.round.asOf}
                        series={updates.figureSeries}
                        thresholdSeries={thresholdSeries}
                        version={reread}
                        previous={panelIndex > 0 ? () => setOpened(drawn[panelIndex - 1], true) : null}
                        next={panelIndex >= 0 && panelIndex < drawn.length - 1 ? () => setOpened(drawn[panelIndex + 1], true) : null}
                        onClose={() => setOpened(null)}
                        onDone={moved}
                      />
                    )
                  }
                >
                  <div className="grid min-w-0 gap-3" onFocus={follow}>
                    <DataTable
                      label={view === 'exceptions' ? 'Exceptions' : 'Advisors who owe figures'}
                      columns={
                        beside
                          ? shown.filter((column) => !PANEL_HIDES.has(column.id) && !extras.includes(column.id))
                          : shown
                      }
                      rows={answer ? drawn : null}
                      rowKey={rowKeyOf}
                      rowProps={(row) => ({ 'data-row-key': rowKeyOf(row) }) as React.HTMLAttributes<HTMLTableRowElement>}
                      onOpen={openOne}
                      selected={panelRow ? rowKeyOf(panelRow) : null}
                      selectable={{ selected: ticked, onSelect: (keys) => setTicked(new Set(keys)), name: advisor }}
                      state={state}
                      busy={rows.reading && answer !== null}
                      minWidth={beside ? undefined : '64rem'}
                      stickyTop={STICKY_TABLE_TOP}
                    />
                    {answer && <WorkPager {...paging.pager} total={answer.total} busy={rows.reading} sticky />}
                  </div>
                </PaneSplit>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** Where else the row's person owes figures this round — one email can ask for both. */
function AlsoOwes({ entries }: { entries: readonly IAlsoOwes[] }) {
  if (entries.length === 0) return null;
  return (
    <span className="mt-1 text-[0.75rem]" data-also-owes={entries.map(pairKey).join(' ')}>
      <span className="text-muted-foreground">Also owes: </span>
      {entries.map((entry, index) => (
        <React.Fragment key={pairKey(entry)}>
          {index > 0 && ', '}
          <Link className="underline-offset-2 hover:underline" to={recordSurveyPath(entry.participantId, entry.survey)}>
            {listSurveyLabel(entry.survey)}
            {entry.relation === 'same' ? '' : ` · ${entry.displayName}`}
          </Link>
          <span className="text-muted-foreground"> ({relationWords(entry.relation)})</span>
        </React.Fragment>
      ))}
    </span>
  );
}

/** An edition as a row's chip names it — its list's label, else the codes. */
function editionLabel(updates: Nullable<IMetricsUpdates>, key: IMetricsEditionKey): string {
  const found = updates?.editions.find((entry) => entry.list === key.list && entry.year === key.year);
  return found?.listLabel ?? `${key.list} ${key.year}`;
}

/** The series every edition read names, once each — what "Enter figures…" asks for. */
function uniqueSeries(editions: readonly IMetricsEdition[]): IThresholdCondition[] {
  const seen = new Map<string, IThresholdCondition>();
  for (const entry of editions)
    for (const condition of conditionsOf(entry.metricThreshold))
      seen.set(seriesKey(condition), condition);
  return [...seen.values()];
}

/** The round in a few words, or why there is none. */
function RoundLine({ updates }: { updates: IMetricsUpdates }) {
  if (!updates.round) {
    return (
      <p className="text-sm text-muted-foreground" data-metrics-round="none">
        No cycle pinning this survey has an as-of date, so there is no round of figures to collect.
        Set one on Setup → Cycles.
      </p>
    );
  }
  const { round } = updates;
  return (
    <span data-metrics-round={round.cycleCode}>
      Collecting figures as of <strong>{formatDay(round.asOf)}</strong> · {round.cycleName}
    </span>
  );
}

/** The page's summary line — the round (`lead`), the editions read, the stage's totals — with the
 *  round's editions under it on asking, each ticked unless the URL unticks it, with its threshold in
 *  words and the way to its edition's Metrics threshold tab; one with no threshold drawn unticked
 *  and off. */
function EditionPicker({
  updates,
  off,
  onToggle,
  lead,
  totals
}: {
  updates: IMetricsUpdates;
  off: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  lead: React.ReactNode;
  totals: React.ReactNode;
}) {
  if (updates.editions.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {lead}
        <p className="text-muted-foreground" data-metrics-edition="none">
          No ranking edition runs on this cycle yet. Start one on Rankings to read who owes figures for it.
        </p>
      </div>
    );
  }
  const readableCount = updates.editions.filter((entry) => entry.metricThreshold !== null).length;
  const ticked = updates.editions.filter((entry) => entry.metricThreshold !== null && !off.has(editionKey(entry)));
  const missing = updates.editions.length - readableCount;
  return (
    <EditionsFold
      // Open at first where something wants doing: an edition with no threshold, or none ticked.
      startOpen={missing > 0 || ticked.length === 0}
      lead={lead}
      totals={totals}
      summary={
        <>
          <span className="text-muted-foreground">
            Editions {ticked.length} of {updates.editions.length} read
          </span>
          {ticked.map((entry) => (
            <StatusChip key={editionKey(entry)} tone="neutral">
              {entry.listLabel}
            </StatusChip>
          ))}
          {missing > 0 && (
            <StatusChip tone="attention">
              {missing} without a threshold
            </StatusChip>
          )}
        </>
      }
    >
      <fieldset className="grid gap-1.5 text-sm" data-metrics-editions>
        <legend className="sr-only">Editions of {updates.round?.cycleCode ?? 'the round'}</legend>
        {updates.editions.map((entry) => {
          const key = editionKey(entry);
          const readable = entry.metricThreshold !== null;
          return (
            <div key={key} className="flex flex-wrap items-center gap-x-3 gap-y-1" data-edition={key}>
              <label className="flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={readable && !off.has(key)}
                  disabled={!readable}
                  onChange={(event) => onToggle(key, event.target.checked)}
                />
                {entry.name}
              </label>
              <span className={readable ? '' : 'text-muted-foreground'} data-metrics-threshold>
                {entry.metricThreshold ? thresholdWords(entry.metricThreshold, updates.figureSeries) : 'No threshold — not read'}
              </span>
              <Button asChild size="sm" variant="outline">
                <Link to={rankingPath({ surveyCode: updates.surveyCode, list: entry.list, year: entry.year }, 'threshold')}>
                  {readable ? 'Change threshold' : 'Set threshold'}
                </Link>
              </Button>
            </div>
          );
        })}
      </fieldset>
    </EditionsFold>
  );
}

/** The summary line, the editions folded into it — what is read, chipped — opened on a press to
 *  tick editions and reach their thresholds. A disclosure (`aria-expanded`), not stored: it opens by
 *  itself where an edition lacks a threshold or none is ticked. */
function EditionsFold({
  startOpen,
  lead,
  summary,
  totals,
  children
}: {
  startOpen: boolean;
  lead: React.ReactNode;
  summary: React.ReactNode;
  totals: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(startOpen);
  const bodyId = React.useId();
  return (
    <div className="grid gap-2" data-editions-fold={open ? 'open' : 'closed'}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {lead}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        {summary}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((now) => !now)}
        >
          {open ? 'Hide editions & thresholds' : 'Show editions & thresholds'}
          <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden />
        </Button>
        {totals}
      </div>
      <div id={bodyId} hidden={!open} className="rounded-md border border-border px-3 py-2">
        {children}
      </div>
    </div>
  );
}
